import { useContext, useEffect } from 'react';
import { OptimizelyContext, enums } from '@optimizely/react-sdk';
import type { DecisionListenerPayload } from '@optimizely/optimizely-sdk';
import { DecisionListItem } from '../forwarding/list-var';
import { resolveDecisionIds } from '../forwarding/resolve-ids';
import { getForwardingBuffer } from '../forwarding/forwarding-buffer';
import { shouldSendToTealium } from '../forwarding/send-to-tealium';

export function useExperimentAdobeForwarding() {
	const { optimizely } = useContext(OptimizelyContext);

	useEffect(() => {
		if (!optimizely) return;

		// Dedup + coalescing live in a shared window singleton (see forwarding-buffer).
		const buffer = getForwardingBuffer();
		if (!buffer) return;

		// notificationCenter is ready before onReady; register now to not miss the first impression.
		const listenerId =
			optimizely.notificationCenter.addNotificationListener<DecisionListenerPayload>(
				enums.NOTIFICATION_TYPES.DECISION,
				notification => {
					if (notification.type !== enums.DECISION_NOTIFICATION_TYPES.FLAG) return;
					const info = notification.decisionInfo;
					// Real impressions only, the click/page hooks decide with DISABLE_DECISION_EVENT.
					if (!info.decisionEventDispatched) return;
					if (!shouldSendToTealium(info.variables)) return;

					// optimizely-sdk 5.x omits the IDs from the notification, resolve from the datafile.
					const ids = resolveDecisionIds(
						optimizely.getOptimizelyConfig(),
						info.flagKey,
						info.ruleKey,
						info.variationKey
					);
					const item: DecisionListItem = {
						ruleKey: info.ruleKey,
						variationKey: info.variationKey,
						experimentId: info.experimentId ?? ids.experimentId,
						variationId: info.variationId ?? ids.variationId,
					};

					buffer.add(item);
				}
			);

		return () => {
			optimizely.notificationCenter.removeNotificationListener(listenerId);
		};
	}, [optimizely]);
}
